import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { fadeUp, viewport } from "@/lib/motion";
import { SERVICE_OPTIONS } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", organization: "", email: "", phone: "", services: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.services || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! We'll be in touch shortly to schedule your consultation.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-navy text-[hsl(var(--background))] pt-24 md:pt-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: pitch + contact info */}
          <div>
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
              className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-6">Schedule a Consultation</motion.p>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={1}
              className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-tight mb-8">
              Let's build your funding strategy.
            </motion.h2>
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={2}
              className="text-base md:text-lg font-light leading-relaxed text-[hsl(var(--background)/0.7)] max-w-md mb-12">
              Tell us about your mission and where you'd like to grow. Your first consultation is always free.
            </motion.p>

            <div className="space-y-6">
              <a href="tel:+12816249576" data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="grid place-items-center w-11 h-11 rounded-full border border-[hsl(var(--gold)/0.4)] text-gold"><Phone size={18} /></span>
                <span className="text-[hsl(var(--background)/0.85)] group-hover:text-gold transition-colors">(281) 624-9576</span>
              </a>
              <a href="mailto:LaurenM@faithandgraceadvisoryservices.com" data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="grid place-items-center w-11 h-11 rounded-full border border-[hsl(var(--gold)/0.4)] text-gold"><Mail size={18} /></span>
                <span className="text-[hsl(var(--background)/0.85)] group-hover:text-gold transition-colors break-all">LaurenM@faithandgraceadvisoryservices.com</span>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <motion.form variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
            onSubmit={submit} data-testid="inquiry-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Name *">
                <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="Jane Doe" className="fg-input" />
              </Field>
              <Field label="Organization">
                <Input data-testid="input-organization" value={form.organization} onChange={(e) => set("organization", e.target.value)}
                  placeholder="Optional" className="fg-input" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Email *">
                <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="you@email.com" className="fg-input" />
              </Field>
              <Field label="Phone *">
                <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  placeholder="(000) 000-0000" className="fg-input" />
              </Field>
            </div>
            <Field label="Services Needed *">
              <Select value={form.services} onValueChange={(v) => set("services", v)}>
                <SelectTrigger data-testid="select-services" className="fg-input">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Brief Message *">
              <Textarea data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us about your mission and goals..." rows={5} className="fg-input resize-none" />
            </Field>
            <button type="submit" data-testid="submit-inquiry-btn" disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--gold))] text-primary px-7 py-4 text-sm font-medium hover:bg-[hsl(var(--background))] transition-colors duration-300 disabled:opacity-60">
              {loading ? "Sending..." : "Schedule Your Free Consultation"}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>
        </div>

        {/* Footer */}
        <footer className="mt-24 md:mt-32 border-t border-[hsl(var(--background)/0.15)] py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md p-1.5 inline-flex">
              <img src="/assets/fg-logo.png" alt="Faith & Grace Advisory Services" className="h-10 w-auto" />
            </span>
            <span className="font-serif text-lg">Faith <span className="text-gold">&amp;</span> Grace Advisory Services, LLC</span>
          </div>
          <p className="text-sm text-[hsl(var(--background)/0.6)]">© {new Date().getFullYear()} Faith &amp; Grace Advisory Services. All rights reserved.</p>
        </footer>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs tracking-wide text-[hsl(var(--background)/0.7)]">{label}</Label>
    {children}
  </div>
);
